import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, getDocs, query, limit } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import {
  FaHeart,
  FaEllipsisV,
  FaEllipsisH,
  FaShareAlt,
  FaShoppingBag,
  FaShoppingCart,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaFacebook,
  FaTwitter,
  FaGlobe,
  FaTiktok,
  FaWhatsapp,
  FaPlus,
  FaMinus,
  FaBookmark,
  FaArrowLeft,
  FaApple,
  FaGooglePlay,
} from "react-icons/fa";
import { addToCart, updateCartItemQuantity, getCartItemQuantity, getCartItemCount } from "../../utils/cartUtils";
import "./ProfileStore.css";
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import { useLanguage } from "../../contexts/LanguageContext";
import useAuthListener from "../../hooks/useAuthListener";

// ButtonEditor Component
const ButtonEditor = ({ onClose, onSave, editingButton }) => {
  const [buttonConfig, setButtonConfig] = useState({
    width: 120,
    height: 40,
    color: '#3b82f6',
    text: 'Button',
    link: '',
    x: 50,
    y: 50,
    borderRadius: 8
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isDraggingHue, setIsDraggingHue] = useState(false);
  const [isDraggingSaturation, setIsDraggingSaturation] = useState(false);
  const [isDraggingAlpha, setIsDraggingAlpha] = useState(false);

  // Color picker state
  const [hue, setHue] = useState(220); // 0-360
  const [saturation, setSaturation] = useState(100); // 0-100
  const [lightness, setLightness] = useState(50); // 0-100
  const [alpha, setAlpha] = useState(100); // 0-100

  // Convert HSL to hex
  const hslToHex = (h, s, l) => {
    h = h / 360;
    s = s / 100;
    l = l / 100;
    
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    
    let r, g, b;
    if (s === 0) {
      r = g = b = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }
    
    const toHex = (c) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  // Convert hex to HSL
  const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
        default: h = 0; break;
      }
      h /= 6;
    }
    
    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  // Update color when HSL values change (only when dragging)
  React.useEffect(() => {
    if (isDraggingHue || isDraggingSaturation || isDraggingAlpha) {
      const newColor = hslToHex(hue, saturation, lightness);
      setButtonConfig(prev => ({ ...prev, color: newColor }));
    }
  }, [hue, saturation, lightness, isDraggingHue, isDraggingSaturation, isDraggingAlpha]);

  // Load existing button data when editing
  React.useEffect(() => {
    if (editingButton) {
      const config = {
        width: editingButton.width || 120,
        height: editingButton.height || 40,
        color: editingButton.color || '#3b82f6',
        text: editingButton.text || 'Button',
        link: editingButton.link || '',
        x: editingButton.x || 50,
        y: editingButton.y || 50,
        borderRadius: typeof editingButton.borderRadius === 'number' ? editingButton.borderRadius : 8
      };
      setButtonConfig(config);
      
      // Initialize HSL values from the button color
      const hsl = hexToHsl(config.color);
      setHue(hsl.h);
      setSaturation(hsl.s);
      setLightness(hsl.l);
    } else {
      // Reset to defaults for new button
      const defaultConfig = {
        width: 120,
        height: 40,
        color: '#3b82f6',
        text: 'Button',
        link: '',
        x: 50,
        y: 50,
        borderRadius: 8
      };
      setButtonConfig(defaultConfig);
      
      // Initialize HSL values from default color
      const hsl = hexToHsl(defaultConfig.color);
      setHue(hsl.h);
      setSaturation(hsl.s);
      setLightness(hsl.l);
    }
  }, [editingButton]);

  const handleMouseDown = (e, type) => {
    e.preventDefault();
    setIsDragging(true);
    setDragType(type);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !dragType) return;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;

    if (dragType === 'width') {
      setButtonConfig(prev => ({
        ...prev,
        width: Math.max(60, prev.width + deltaX)
      }));
    } else if (dragType === 'height') {
      setButtonConfig(prev => ({
        ...prev,
        height: Math.max(30, prev.height + deltaY)
      }));
    }

    setDragStart({ x: e.clientX, y: e.clientY });
  }, [isDragging, dragType, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragType(null);
  }, []);

  // Color picker mouse handlers
  const handleHueMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingHue(true);
    
    // Update hue immediately on click
    const hueRect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - hueRect.top;
    const percentage = Math.max(0, Math.min(1, y / hueRect.height));
    setHue(Math.round(percentage * 360));
  };

  const handleSaturationMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingSaturation(true);
    
    // Update saturation and lightness immediately on click
    const satRect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - satRect.left;
    const y = e.clientY - satRect.top;
    const satPercentage = Math.max(0, Math.min(1, x / satRect.width));
    const lightPercentage = Math.max(0, Math.min(1, y / satRect.height));
    setSaturation(Math.round(satPercentage * 100));
    setLightness(Math.round((1 - lightPercentage) * 100));
  };

  const handleAlphaMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAlpha(true);
    
    // Update alpha immediately on click
    const alphaRect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - alphaRect.top;
    const percentage = Math.max(0, Math.min(1, y / alphaRect.height));
    setAlpha(Math.round(percentage * 100));
  };

  const handleColorPickerMouseMove = useCallback((e) => {
    if (isDraggingHue) {
      const hueRect = document.querySelector('.hue-slider')?.getBoundingClientRect();
      if (hueRect) {
        const y = e.clientY - hueRect.top;
        const percentage = Math.max(0, Math.min(1, y / hueRect.height));
        const newHue = Math.round(percentage * 360);
        if (newHue !== hue) {
          setHue(newHue);
        }
      }
    } else if (isDraggingSaturation) {
      const satRect = document.querySelector('.saturation-lightness-panel')?.getBoundingClientRect();
      if (satRect) {
        const x = e.clientX - satRect.left;
        const y = e.clientY - satRect.top;
        const satPercentage = Math.max(0, Math.min(1, x / satRect.width));
        const lightPercentage = Math.max(0, Math.min(1, y / satRect.height));
        const newSaturation = Math.round(satPercentage * 100);
        const newLightness = Math.round((1 - lightPercentage) * 100);
        
        if (newSaturation !== saturation || newLightness !== lightness) {
          setSaturation(newSaturation);
          setLightness(newLightness);
        }
      }
    } else if (isDraggingAlpha) {
      const alphaRect = document.querySelector('.alpha-slider')?.getBoundingClientRect();
      if (alphaRect) {
        const y = e.clientY - alphaRect.top;
        const percentage = Math.max(0, Math.min(1, y / alphaRect.height));
        const newAlpha = Math.round(percentage * 100);
        if (newAlpha !== alpha) {
          setAlpha(newAlpha);
        }
      }
    }
  }, [isDraggingHue, isDraggingSaturation, isDraggingAlpha, hue, saturation, lightness, alpha]);

  const handleColorPickerMouseUp = useCallback(() => {
    setIsDraggingHue(false);
    setIsDraggingSaturation(false);
    setIsDraggingAlpha(false);
  }, []);

  // Add event listeners for color picker dragging
  React.useEffect(() => {
    if (isDraggingHue || isDraggingSaturation || isDraggingAlpha) {
      document.addEventListener('mousemove', handleColorPickerMouseMove);
      document.addEventListener('mouseup', handleColorPickerMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleColorPickerMouseMove);
        document.removeEventListener('mouseup', handleColorPickerMouseUp);
      };
    }
  }, [isDraggingHue, isDraggingSaturation, isDraggingAlpha, handleColorPickerMouseMove, handleColorPickerMouseUp]);

  const handleSave = () => {
    if (editingButton) {
      // Update existing button
      const updatedButton = {
        ...buttonConfig,
        id: editingButton.id // Keep the same ID
      };
      onSave(updatedButton, 'update');
    } else {
      // Create new button
      const newButton = {
        ...buttonConfig,
        id: Date.now().toString()
      };
      onSave(newButton, 'create');
    }
  };

  // Add event listeners for mouse events
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragType, dragStart, handleMouseMove, handleMouseUp]);

  return (
    <div className="button-editor-overlay">
      <div className="button-editor-modal">
        <div className="button-editor-header">
          <h2>{editingButton ? 'Edit Button' : 'Create Custom Button'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="button-editor-content">
          {/* Button Preview */}
          <div className="button-preview-section">
            <h3>Preview</h3>
            <div className="button-preview-container">
              <div
                className="button-preview"
                style={{
                  width: buttonConfig.width,
                  height: buttonConfig.height,
                  backgroundColor: buttonConfig.color,
                  borderRadius: `${buttonConfig.borderRadius}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              >
                {buttonConfig.text || 'Button'}
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="button-controls">
            {/* Dimensions */}
            <div className="control-group">
              <h4>Dimensions</h4>
              <div className="dimension-controls">
                <div className="dimension-input">
                  <label>Width: {buttonConfig.width}px</label>
                  <div 
                    className="resize-handle"
                    onMouseDown={(e) => handleMouseDown(e, 'width')}
                    style={{ cursor: 'ew-resize' }}
                  >
                    ↔
                  </div>
                </div>
                <div className="dimension-input">
                  <label>Height: {buttonConfig.height}px</label>
                  <div 
                    className="resize-handle"
                    onMouseDown={(e) => handleMouseDown(e, 'height')}
                    style={{ cursor: 'ns-resize' }}
                  >
                    ↕
                  </div>
                </div>
              </div>
            </div>

            {/* Color Picker */}
            <div className="control-group">
              <h4>Color</h4>
              <div className="color-picker-section">
                <button 
                  className="color-preview-btn"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  style={{ backgroundColor: buttonConfig.color }}
                >
                  {buttonConfig.color}
                </button>
                {showColorPicker && (
                  <div className="color-picker-popup">
                    <div className="color-picker-container">
                      {/* Saturation/Lightness Panel */}
                      <div 
                        className="saturation-lightness-panel"
                        style={{
                          background: `linear-gradient(to right, white, hsl(${hue}, 100%, 50%)), linear-gradient(to top, black, transparent)`
                        }}
                        onMouseDown={handleSaturationMouseDown}
                      >
                        <div 
                          className="saturation-lightness-cursor"
                          style={{
                            left: `${saturation}%`,
                            top: `${100 - lightness}%`,
                            backgroundColor: hslToHex(hue, saturation, lightness)
                          }}
                        />
                      </div>
                      
                      {/* Hue Slider */}
                      <div 
                        className="hue-slider"
                        onMouseDown={handleHueMouseDown}
                      >
                        <div 
                          className="hue-cursor"
                          style={{
                            top: `${(hue / 360) * 100}%`
                          }}
                        />
                      </div>
                      
                      {/* Alpha Slider */}
                      <div 
                        className="alpha-slider"
                        style={{
                          background: `linear-gradient(to bottom, ${hslToHex(hue, saturation, lightness)}, transparent)`
                        }}
                        onMouseDown={handleAlphaMouseDown}
                      >
                        <div 
                          className="alpha-cursor"
                          style={{
                            top: `${100 - alpha}%`
                          }}
                        />
                      </div>
                    </div>
                    
                    {/* Color Input */}
                    <div className="color-input-section">
                      <input
                        type="text"
                        value={buttonConfig.color}
                        onChange={(e) => {
                          const color = e.target.value;
                          if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
                            const hsl = hexToHsl(color);
                            setHue(hsl.h);
                            setSaturation(hsl.s);
                            setLightness(hsl.l);
                            setButtonConfig(prev => ({ ...prev, color }));
                          }
                        }}
                        onBlur={(e) => {
                          const color = e.target.value;
                          if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
                            const hsl = hexToHsl(color);
                            setHue(hsl.h);
                            setSaturation(hsl.s);
                            setLightness(hsl.l);
                            setButtonConfig(prev => ({ ...prev, color }));
                          } else {
                            // Reset to current color if invalid
                            e.target.value = buttonConfig.color;
                          }
                        }}
                        className="color-hex-input"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Border Radius */}
            <div className="control-group">
              <h4>Border Radius</h4>
              <div className="border-radius-controls">
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={buttonConfig.borderRadius}
                  onChange={(e) => setButtonConfig(prev => ({ ...prev, borderRadius: parseInt(e.target.value) }))}
                  className="border-radius-slider"
                />
                <span className="border-radius-value">{buttonConfig.borderRadius}px</span>
              </div>
            </div>

            {/* Text Input */}
            <div className="control-group">
              <h4>Button Text</h4>
              <input
                type="text"
                value={buttonConfig.text}
                onChange={(e) => setButtonConfig(prev => ({ ...prev, text: e.target.value }))}
                placeholder="Enter button text"
                className="text-input"
              />
            </div>

            {/* Link Input */}
            <div className="control-group">
              <h4>Link URL</h4>
              <input
                type="url"
                value={buttonConfig.link}
                onChange={(e) => setButtonConfig(prev => ({ ...prev, link: e.target.value }))}
                placeholder="https://example.com"
                className="link-input"
              />
            </div>

            {/* Position Controls */}
            <div className="control-group">
              <h4>Position</h4>
              <div className="position-controls">
                <div className="position-input">
                  <label>X Position: {buttonConfig.x}px</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={buttonConfig.x}
                    onChange={(e) => setButtonConfig(prev => ({ ...prev, x: parseInt(e.target.value) }))}
                    className="position-slider"
                  />
                </div>
                <div className="position-input">
                  <label>Y Position: {buttonConfig.y}px</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={buttonConfig.y}
                    onChange={(e) => setButtonConfig(prev => ({ ...prev, y: parseInt(e.target.value) }))}
                    className="position-slider"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="button-editor-footer">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="save-btn" onClick={handleSave}>
            {editingButton ? 'Update Button' : 'Create Button'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ProductCard Component
const ProductCard = ({ product, navigate, isAuthenticated, onUnauthenticatedClick }) => {
  const { translations: tRaw } = useLanguage();
  const t = { ...tRaw, profileStore: { ...tRaw?.profileStore } };
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [cartQuantity, setCartQuantity] = useState(0);
  const images = product.Images || [product.Image] || [];
  
  // Check if product is in cart and get quantity
  useEffect(() => {
    const quantity = getCartItemQuantity(product.id);
    setCartQuantity(quantity);
  }, [product.id]);
  
  // Auto-rotate images on hover
  useEffect(() => {
    let interval;
    if (isHovering && images.length > 1) {
      interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }, 5000); // Change every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isHovering, images.length]);
  
  // Close options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOptions && !event.target.closest('.product-options')) {
        setShowOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptions]);
  
  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const toggleLike = (e) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    addToCart(product);
    setCartQuantity(prev => prev + 1);
  };

  const handleQuantityChange = (e, change) => {
    e.stopPropagation();
    updateCartItemQuantity(product.id, change);
    setCartQuantity(prev => {
      const newQuantity = prev + change;
      return newQuantity <= 0 ? 0 : newQuantity;
    });
  };

  const toggleOptions = (e) => {
    e.stopPropagation();
    setShowOptions(!showOptions);
  };

  const handleSave = (e) => {
    e.stopPropagation();
    setShowOptions(false);
    console.log('Save product:', product.id);
  };

  const handleShare = (e) => {
    e.stopPropagation();
    setShowOptions(false);
    console.log('Share product:', product.id);
  };

  // Calculate discount percentage
  const discountPercentage = product.SalePrice && product.Price && product.SalePrice !== product.Price
    ? Math.round(((product.Price - product.SalePrice) / product.Price) * 100)
    : 0;

  const handleProductClick = () => {
    if (!isAuthenticated) {
      onUnauthenticatedClick();
    } else {
      navigate(`/product-details/${product.VendorId}/${product.id}`);
    }
  };

  return (
    <div 
      className="product-card"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={handleProductClick}
    >
      <div className="profile-product-image-container">
        <img
          src={images[currentImageIndex]}
          alt={product.Title || product.ArabicTitle}
          className="profile-product-image"
          onError={(e) => {
            e.currentTarget.src = "/default-product.png";
          }}
        />
        
        {/* Discount Badge */}
        {discountPercentage > 0 && (
          <div className="discount-badge">
            -{discountPercentage}%
          </div>
        )}
        
        {/* Like Button - Only show for authenticated users */}
        {isAuthenticated && (
          <button className="product-like-btn" onClick={toggleLike}>
            <FaHeart className={isLiked ? 'liked' : ''} />
          </button>
        )}
        
        {/* Add to Cart Button / Quantity Selector - Only show for authenticated users */}
        {isAuthenticated && (
          cartQuantity === 0 ? (
            <button className="product-add-btn" onClick={handleAddToCart}>
              <FaPlus />
            </button>
          ) : (
            <div className="product-quantity-selector">
              <button 
                className="quantity-btn minus" 
                onClick={(e) => handleQuantityChange(e, -1)}
              >
                <FaMinus />
              </button>
              <span className="quantity-value">{cartQuantity}</span>
              <button 
                className="quantity-btn plus" 
                onClick={(e) => handleQuantityChange(e, 1)}
              >
                <FaPlus />
              </button>
            </div>
          )
        )}
        
        {/* Image Pagination Dots */}
        {images.length > 1 && (
          <div className="product-dots">
            {images.map((_, index) => (
              <button
                key={index}
                className={`product-dot ${index === currentImageIndex ? 'active' : ''}`}
                onClick={() => goToImage(index)}
              />
            ))}
          </div>
        )}
      </div>
      
      <div className="product-info">
        <h3 className="product-title">{product.Title || product.ArabicTitle}</h3>
        <p className="product-description">{product.Description || product.ArabicDescription}</p>
        <div className="product-price">
          {product.SalePrice && product.SalePrice !== product.Price && (
            <span className="product-original-price">${Number(product.Price ?? 0).toFixed(2)}</span>
          )}
          <span className="product-current-price">
            ${Number((product.SalePrice ?? product.Price) ?? 0).toFixed(2)}
          </span>
        </div>
        
        {/* Options Menu */}
        <div className="product-options">
          <button className="product-options-btn" onClick={toggleOptions}>
            <FaEllipsisH />
          </button>
          {showOptions && (
            <div className="product-options-menu">
              <button onClick={handleSave}>
                <FaBookmark /> {t.profileStore?.productOptions?.save || "Save"}
              </button>
              <button onClick={handleShare}>
                <FaShareAlt /> {t.profileStore?.productOptions?.share || "Share"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function ProfileStore() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { translations: tRaw } = useLanguage();
  const t = { ...tRaw, profileStore: { ...tRaw?.profileStore } };
  const { user: currentUser } = useAuthListener();

  // Always build a deterministic, canonical store URL for sharing/QR
  // Prefer env override; default to production domain
  const PUBLIC_BASE_URL = (process.env.REACT_APP_PUBLIC_BASE_URL || 'https://iwinto.com').replace(/\/$/, '');
  const buildStoreUrl = (uid) => `${PUBLIC_BASE_URL}/${uid}/store`;

  const [userData, setUserData] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasProducts, setHasProducts] = useState(false);
  const [banners, setBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [categories, setCategories] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [sectorProducts, setSectorProducts] = useState({});
  const [loadingSectorProducts, setLoadingSectorProducts] = useState(false);
  const [totalProductCount, setTotalProductCount] = useState(0);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [showButtonEditor, setShowButtonEditor] = useState(false);
  const [editingButton, setEditingButton] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const hydratedRef = useRef(false);
  const [draggingButton, setDraggingButton] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Check if user is authenticated
  const isAuthenticated = !!currentUser;
  
  // Check if current user is viewing their own store
  const isOwnStore = currentUser && currentUser.uid === id;

  // Handle fullscreen image functions
  const openFullscreen = (imageSrc) => {
    if (imageSrc) {
      setFullscreenImage(imageSrc);
      document.body.style.overflow = 'hidden';
    }
  };

  const closeFullscreen = () => {
    setFullscreenImage(null);
    document.body.style.overflow = 'unset';
  };

  // Initialize customButtons with localStorage data immediately
  const [customButtons, setCustomButtons] = useState(() => {
    try {
      const savedButtons = localStorage.getItem(`customButtons_${id}`);
      if (savedButtons) {
        const parsed = JSON.parse(savedButtons);
        console.log('[BUTTONS] Initial state loaded:', parsed);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      console.error('[BUTTONS] Error in initial state load:', error);
    }
    console.log('[BUTTONS] No saved buttons, starting with empty array');
    return [];
  });

  // Set hydration flag immediately since we loaded from localStorage
  useEffect(() => {
    hydratedRef.current = true;
    console.log('[BUTTONS] Hydration flag set to true for store:', id);
    
    // Debug: Check what's actually in localStorage
    const stored = localStorage.getItem(`customButtons_${id}`);
    console.log('[BUTTONS] Current localStorage content:', stored);
    console.log('[BUTTONS] Current component state:', customButtons);
  }, [id, customButtons]);

  // Save buttons to localStorage whenever customButtons changes
  useEffect(() => {
    console.log('[BUTTONS] Save effect triggered, buttons count:', customButtons.length);
    try {
      const data = JSON.stringify(customButtons);
      localStorage.setItem(`customButtons_${id}`, data);
      console.log('[BUTTONS] Successfully saved', customButtons.length, 'buttons to localStorage');
    } catch (error) {
      console.error('[BUTTONS] Error saving buttons:', error);
    }
  }, [customButtons, id]);

  // Handle escape key and click outside for fullscreen
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && fullscreenImage) {
        closeFullscreen();
      }
    };

    if (fullscreenImage) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [fullscreenImage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Helper function to immediately save buttons (for critical operations)
  const saveButtonsImmediately = useCallback((buttons) => {
    try {
      const data = JSON.stringify(buttons);
      localStorage.setItem(`customButtons_${id}`, data);
      console.log('[BUTTONS] Immediate save:', buttons.length, 'buttons');
    } catch (error) {
      console.error('[BUTTONS] Error in immediate save:', error);
    }
  }, [id]);

  useEffect(() => {
    let isMounted = true;

    const hasAnyProduct = async (userId) => {
      try {
        // 1) Fast path: known org id "1" with "Products" subcollection
        const productsRefKnown = collection(db, "users", userId, "organization", "1", "Products");
        const oneKnown = await getDocs(query(productsRefKnown, limit(1)));
        if (!oneKnown.empty) return true;

        // 2) Fallback: scan all org docs in case some profiles used a different id
        const orgsRef = collection(db, "users", userId, "organization");
        const orgsSnap = await getDocs(orgsRef);
        if (orgsSnap.empty) return false;

        for (const orgDoc of orgsSnap.docs) {
          const orgId = orgDoc.id;

          const productsRef = collection(db, "users", userId, "organization", orgId, "Products");
          const one = await getDocs(query(productsRef, limit(1)));
          if (!one.empty) return true;
        }
        return false;
      } catch (e) {
        console.error("Error while checking Products:", e);
        return false;
      }
    };

    const run = async () => {
      try {
        const userRef = doc(db, "users", id);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
          navigate("/feed");
          return;
        }

        const data = userSnap.data();
        if (!isMounted) return;
        setUserData(data);

        // If you store a product count and it's > 0, treat as having products immediately
        const productCount =
          (data.storeStats && data.storeStats.productCount) ??
          data.productsCount ??
          null;

        if (typeof productCount === "number" && productCount > 0) {
          if (isMounted) setHasProducts(true);
                 } else {
           const found = await hasAnyProduct(id);
           if (isMounted) setHasProducts(found);
         }

                   // Fetch banners
          try {
            const bannersRef = collection(db, "users", id, "organization", "1", "Banners");
            const bannersSnap = await getDocs(bannersRef);
            
            if (!bannersSnap.empty) {
              const bannersData = bannersSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              })).sort((a, b) => (a.order || 0) - (b.order || 0));
              
              // Filter out banners without images and map Image field correctly
              const validBanners = bannersData.filter(banner => banner.Image || banner.image);
              
              if (isMounted) setBanners(validBanners);
            }
          } catch (bannerError) {
            console.error("Error fetching banners:", bannerError);
          }

                     // Fetch categories
           try {
             const categoriesRef = collection(db, "users", id, "organization", "1", "category");
             const categoriesSnap = await getDocs(categoriesRef);
             
             if (!categoriesSnap.empty) {
               const categoriesData = categoriesSnap.docs.map(doc => {
                 const data = doc.data();
                 return {
                   id: doc.id,
                   ...data,
                   // Ensure we have the correct name field
                   displayName: data.Name || data.name || data.ArabicName || 'Unnamed Category'
                 };
               }).sort((a, b) => {
                 // Sort by IsFeature first (featured categories first), then by Name
                 if (a.IsFeature && !b.IsFeature) return -1;
                 if (!a.IsFeature && b.IsFeature) return 1;
                 return (a.displayName || '').localeCompare(b.displayName || '');
               });
               
               console.log("Categories data:", categoriesData); // Debug log
               
               if (isMounted) setCategories(categoriesData);
             }
           } catch (categoryError) {
             console.error("Error fetching categories:", categoryError);
           }

           // Fetch sectors
           try {
             const sectorsRef = collection(db, "users", id, "organization", "1", "sectors");
             const sectorsSnap = await getDocs(sectorsRef);
             
             if (!sectorsSnap.empty) {
               const sectorsData = sectorsSnap.docs.map(doc => {
                 const data = doc.data();
                 return {
                   id: doc.id,
                   ...data,
                   // Use name field for display
                   displayName: data.name || data.englishName || 'Unnamed Sector'
                 };
               });
               
               // Remove duplicate sectors based on name or englishName
               const uniqueSectors = sectorsData.filter((sector, index, self) => {
                 const sectorName = sector.name || '';
                 const sectorEnglishName = sector.englishName || '';
                 
                 return index === self.findIndex(s => 
                   (s.name === sectorName && sectorName !== '') ||
                   (s.englishName === sectorEnglishName && sectorEnglishName !== '') ||
                   s.id === sector.id
                 );
               }).sort((a, b) => {
                 return (a.displayName || '').localeCompare(b.displayName || '');
               });
               
               console.log("Sectors data (duplicates removed):", uniqueSectors); // Debug log
               
               if (isMounted) setSectors(uniqueSectors);
             }
           } catch (sectorError) {
             console.error("Error fetching sectors:", sectorError);
           }
      } catch (err) {
        console.error("Error fetching user data:", err);
        navigate("/feed");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

         run();
     return () => { isMounted = false; };
   }, [id, navigate]);

  // Fetch all products and group them by sectors
  const fetchSectorProducts = useCallback(async () => {
    setLoadingSectorProducts(true);
    
    try {
      // Fetch products from the main products collection
      const productsRef = collection(db, "products");
      const productsSnap = await getDocs(productsRef);
      
      if (!productsSnap.empty) {
        const productsData = productsSnap.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(product => {
            // Filter by vendor ID (store owner)
            return product.VendorId === id;
          });

        // Remove duplicate products based on product ID
        const uniqueProducts = productsData.filter((product, index, self) => 
          index === self.findIndex(p => p.id === product.id)
        );

        // Set total product count
        setTotalProductCount(uniqueProducts.length);
        
        // Get cart item count
        const cartCount = getCartItemCount();
        setCartItemCount(cartCount);

        // Group products by sectors and ensure no duplicates
        const groupedProducts = {};
        const usedProductIds = new Set();
        
        sectors.forEach(sector => {
          const sectorProducts = uniqueProducts.filter(product => {
            // Skip if product already used in another sector
            if (usedProductIds.has(product.id)) {
              return false;
            }
            
            // Check if product's ProductType matches sector's name or englishName
            const productType = product.ProductType || '';
            const sectorName = sector.name || '';
            const sectorEnglishName = sector.englishName || '';
            
            const matches = productType.toLowerCase() === sectorName.toLowerCase() || 
                           productType.toLowerCase() === sectorEnglishName.toLowerCase();
            
            // If matches, mark product as used
            if (matches) {
              usedProductIds.add(product.id);
            }
            
            return matches;
          });
          
          if (sectorProducts.length > 0) {
            groupedProducts[sector.id] = {
              sector: sector,
              products: sectorProducts
            };
          }
        });
        
        setSectorProducts(groupedProducts);
        console.log("Sector products grouped (duplicates removed):", groupedProducts);
        console.log("Total product count:", uniqueProducts.length);
      } else {
        setSectorProducts({});
        setTotalProductCount(0);
      }
    } catch (error) {
      console.error("Error fetching sector products:", error);
      setSectorProducts({});
      setTotalProductCount(0);
    } finally {
      setLoadingSectorProducts(false);
    }
  }, [sectors, id]);

  // Fetch sector products when sectors are loaded
  useEffect(() => {
    if (sectors.length > 0) {
      fetchSectorProducts();
    }
  }, [sectors, id, fetchSectorProducts]);

   // Close dropdown when clicking outside
   useEffect(() => {
     const handleClickOutside = (event) => {
       if (menuOpen && !event.target.closest('.ps-actions') && !event.target.closest('.ps-dropdown')) {
         setMenuOpen(false);
       }
     };

     document.addEventListener('mousedown', handleClickOutside);
     return () => {
       document.removeEventListener('mousedown', handleClickOutside);
     };
   }, [menuOpen]);

  const toggleFavorite = () => setIsFavorite((v) => !v);
  const toggleMenu = () => setMenuOpen((v) => !v);
  const toggleEditMode = () => {
    console.log('Current edit mode:', editMode);
    console.log('Toggling edit mode to:', !editMode);
    setEditMode((v) => !v);
    console.log('Edit mode state updated');
  };

  const nextBanner = () => {
    setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBannerIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const goToBanner = (index) => {
    setCurrentBannerIndex(index);
  };

  const fetchCategoryProducts = async (categoryId, categoryName) => {
    setLoadingProducts(true);
    setSelectedCategory({ id: categoryId, name: categoryName });
    setSelectedCategoryId(categoryId);
    
    try {
      // Fetch products from the main products collection
      const productsRef = collection(db, "products");
      const productsSnap = await getDocs(productsRef);
      
      if (!productsSnap.empty) {
        const productsData = productsSnap.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(product => {
            // Filter by vendor ID (store owner)
            const isFromStore = product.VendorId === id;
            
            // Filter by category - check if product has Category field and matches
            const hasMatchingCategory = product.Category && 
              (product.Category.Id === categoryId || product.Category.Name === categoryName);
            
            return isFromStore && hasMatchingCategory;
          });
        
        setCategoryProducts(productsData);
      } else {
        setCategoryProducts([]);
      }
    } catch (error) {
      console.error("Error fetching category products:", error);
      setCategoryProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const clearCategorySelection = () => {
    setSelectedCategory(null);
    setCategoryProducts([]);
    setSelectedCategoryId(null);
  };


  const handleShare = async () => {
    // Deterministically build the exact store URL and share via text
    const storeUrl = buildStoreUrl(id);

    try {
      if (navigator.share) {
        await navigator.share({
          title: userData?.organizationName || userData?.username || 'Store',
          // Provide both fields so Windows share shows QR/Copy Link and
          // both encode the exact deterministic store URL
          text: storeUrl,
          url: storeUrl
        });
      } else {
        await navigator.clipboard.writeText(storeUrl);
        alert('Store link copied!');
      }
    } catch (e) {
      console.error('Share error:', e);
    }
  };

  // Handle button dragging for positioning
  const handleButtonMouseDown = (e, buttonId) => {
    if (!editMode) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const rawX = e.clientX - centerX;
    const rawY = e.clientY - centerY;
    // Keep exact offset so the button stays in place under the cursor on grab
    setDragOffset({ x: rawX, y: rawY });
    setDraggingButton(buttonId);
  };

  const handleButtonMouseMove = useCallback((e) => {
    if (!draggingButton) return;

    const bannerRect = document.querySelector('.ps-banner')?.getBoundingClientRect();
    if (!bannerRect) return;

    const update = () => {
      const relativeX = ((e.clientX - dragOffset.x - bannerRect.left) / bannerRect.width) * 100;
      const relativeY = ((e.clientY - dragOffset.y - bannerRect.top) / bannerRect.height) * 100;

      const clampedX = Math.max(0, Math.min(100, relativeX));
      const clampedY = Math.max(0, Math.min(100, relativeY));

      setCustomButtons(prev => prev.map(button => 
        button.id === draggingButton 
          ? { ...button, x: clampedX, y: clampedY }
          : button
      ));
    };
    if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
      requestAnimationFrame(update);
    } else {
      update();
    }
  }, [draggingButton, dragOffset]);

  const handleButtonMouseUp = useCallback(() => {
    setDraggingButton(null);
    setDragOffset({ x: 0, y: 0 });
    // Immediately save current button positions after drag
    saveButtonsImmediately(customButtons);
    console.log('[BUTTONS] Drag ended, saved positions immediately');
  }, [customButtons, saveButtonsImmediately]);

  // Add event listeners for button dragging
  useEffect(() => {
    if (draggingButton) {
      document.addEventListener('mousemove', handleButtonMouseMove);
      document.addEventListener('mouseup', handleButtonMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleButtonMouseMove);
        document.removeEventListener('mouseup', handleButtonMouseUp);
      };
    }
  }, [draggingButton, handleButtonMouseMove, handleButtonMouseUp]);

  // Handle button click - fix URL opening or editing
  const handleButtonClick = (button, e) => {
    if (editMode) {
      // In edit mode, clicking the button opens the editor
      e.stopPropagation();
      setEditingButton(button);
      setShowButtonEditor(true);
    } else {
      // In normal mode, open the link
      if (button.link) {
        // Ensure the URL has a protocol
        let url = button.link;
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  // Delete button function
  const deleteButton = (buttonId) => {
    const newButtons = customButtons.filter(button => button.id !== buttonId);
    setCustomButtons(newButtons);
    saveButtonsImmediately(newButtons);
    console.log('[BUTTONS] Button deleted:', buttonId);
  };

  const normalizeUrl = (u) =>
    !u ? "" : u.startsWith("http://") || u.startsWith("https://") ? u : `https://${u}`;

  const handleBannerImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Here you would typically upload the file and update the user data
      console.log('Banner image selected:', file);
      // For now, we'll just close edit mode
      setEditMode(false);
    }
  };

  const handleLogoImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Here you would typically upload the file and update the user data
      console.log('Logo image selected:', file);
      // For now, we'll just close edit mode
      setEditMode(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-animation"></div>
      </div>
    );
  }

  if (!userData) return <div className="not-found">{t.profileStore?.storeNotFound || "Store not found"}</div>;

  // ----- mapped fields -----
  const bannerSrc = userData.bannerImage || "";
  const orgLogo =
    userData.compressedOrganizationLogo ||
    userData.organizationLogo ||
    userData.profileImage ||
    "/default-avatar.png";
  const storeName =
    userData.organizationName || userData.name || userData.username || "Store";
  const category = userData.category || userData.profession || "";
  const brief = typeof userData.brief === "string" ? userData.brief.trim() : "";
  const social = userData.socialLinks || {};

  return (
    <div className="profile-store-wrapper">
      <FloatingSidebarButton userProfile={{ name: userData?.username || userData?.organizationName, profileImage: userData?.profileImage || userData?.organizationLogo }} />
      <div className="profile-store-container">
        {/* Back Button */}
        <button 
          className="ps-back-button" 
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <FaArrowLeft />
        </button>
        {/* Banner */}
        <div 
          className={`ps-banner ${editMode ? 'edit-mode' : ''}`}
          style={{ '--banner-url': bannerSrc ? `url(${bannerSrc})` : 'none' }}
        >
          {bannerSrc ? (
            <img 
              src={bannerSrc} 
              alt="Banner" 
              className="ps-banner-image"
              onClick={() => openFullscreen(bannerSrc)}
              style={{ cursor: 'pointer' }}
            />
          ) : (
            <div className="ps-banner-placeholder" />
          )}
          <div className="ps-banner-gradient" />
          
          {/* Banner Edit Overlay */}
          {editMode && (
            <div className="ps-edit-overlay">
              <input
                type="file"
                accept="image/*"
                onChange={handleBannerImageChange}
                style={{ display: 'none' }}
                id="banner-upload"
              />
              <label htmlFor="banner-upload" className="ps-edit-button">
                <FaPlus />
                <span>Edit Banner</span>
              </label>
            </div>
          )}
          
          {/* Edit Mode Indicator */}
          {editMode && (
            <div className="ps-edit-mode-indicator">
              <span>Edit Mode Active</span>
              <button 
                className="ps-exit-edit-btn" 
                onClick={toggleEditMode}
                title="Exit Edit Mode"
              >
                ×
              </button>
            </div>
          )}

          {/* actions (top-right) */}
          <div className="ps-actions">
        <button className="ps-action-btn" onClick={toggleMenu} aria-label={t.profileStore?.options?.exit || "Options"}>
              <FaEllipsisV />
            </button>
        <button className="ps-action-btn" onClick={handleShare} aria-label={t.profileStore?.options?.share || "Share"}>
              <FaShareAlt />
            </button>
            {/* Only show favorite button for authenticated users */}
            {isAuthenticated && (
              <button
                className={`ps-action-btn ${isFavorite ? "active" : ""}`}
                onClick={toggleFavorite}
                aria-label={t.profileStore?.options?.save || "Favorite"}
                title={t.profileStore?.options?.save || "Favorite"}
              >
                <FaHeart />
              </button>
            )}
            {/* Only show cart button for authenticated users */}
            {isAuthenticated && (
              <button
                className="ps-action-btn cart-btn"
                onClick={() => navigate(`/cart/${id}`)}
                aria-label={t.profileStore?.viewCart || "View Cart"}
                title={t.profileStore?.viewCart || "View Cart"}
              >
                <FaShoppingCart />
                {cartItemCount > 0 && (
                  <span className="cart-indicator">{cartItemCount}</span>
                )}
              </button>
            )}
          </div>

                     {/* Products button (bottom-right of banner) */}
           <button
             className="ps-products-button"
             onClick={() => navigate(`/products/${id}`)}
            title={t.profileStore?.viewProducts || "View Products"}
           >
             {totalProductCount > 0 && (
               <span className="ps-products-count">{totalProductCount}</span>
             )}
             <FaShoppingBag className="ps-products-icon" />
           </button>

          {/* Owner avatar moved above Products button (bottom-right) */}
          {/* <img
            src={ownerAvatar}
            alt="Owner"
            className="ps-owner-avatar"
            draggable="false"
            onError={(e) => {
              e.currentTarget.src = "/default-avatar.png";
            }}
            onClick={() => navigate(`/profile/${id}`)}
            title={t.profileStore?.viewOwnerProfile || "View Owner Profile"}
          /> */}

          {/* avatar stack (bottom-left overlap) */}
          <div className="ps-avatars">
            <div className="ps-logo-container">
              <img
                src={orgLogo}
                alt="Organization Logo"
                className={`ps-org-logo ${editMode ? 'edit-mode' : ''}`}
                draggable="false"
                onClick={() => openFullscreen(orgLogo)}
                style={{ cursor: 'pointer' }}
                onError={(e) => {
                  e.currentTarget.src = "/default-avatar.png";
                }}
              />
              
              {/* Logo Edit Overlay */}
              {editMode && (
                <div className="ps-logo-edit-overlay ps-logo-edit-overlay-active">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoImageChange}
                    style={{ display: 'none' }}
                    id="logo-upload"
                  />
                  <label htmlFor="logo-upload" className="ps-logo-edit-button">
                    <FaPlus />
                  </label>
                </div>
              )}
            </div>
          </div>

          {menuOpen && (
            <div className="ps-dropdown">
              {/* Only show Edit Store option if viewing your own store */}
              {isOwnStore && (
                <>
                  <button className="ps-dropdown-item" onClick={() => { 
                    console.log('Edit Store button clicked');
                    toggleEditMode(); 
                    setMenuOpen(false); 
                  }}>
                    {t?.profileStore?.dropdown?.editStore || "Edit Store"}
                  </button>
                  <button className="ps-dropdown-item" onClick={() => { 
                    setEditingButton(null); // Clear any editing state
                    setShowButtonEditor(true); 
                    setMenuOpen(false); 
                  }}>
                    {t?.profileStore?.dropdown?.createButton || "Create Button"}
                  </button>
                </>
              )}
              <button className="ps-dropdown-item" onClick={() => navigate(-1)}>
                {t?.profileStore?.dropdown?.exit || "Exit"}
              </button>
            </div>
          )}
        </div>

        {/* identity */}
        <div className="ps-identity">
          <h1 className="ps-name">{storeName}</h1>
          {category && <div className="ps-category">{category}</div>}
          {brief && <p className="ps-brief">{brief}</p>}
        </div>

        {/* social links */}
        <div className="ps-social">
          {social.instagram && (
            <a href={normalizeUrl(social.instagram)} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <FaInstagram />
            </a>
          )}
          {social.youtube && (
            <a href={normalizeUrl(social.youtube)} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
              <FaYoutube />
            </a>
          )}
          {social.facebook && (
            <a href={normalizeUrl(social.facebook)} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <FaFacebook />
            </a>
          )}
          {social.twitter && (
            <a href={normalizeUrl(social.twitter)} target="_blank" rel="noopener noreferrer" aria-label="Twitter / X">
              <FaTwitter />
            </a>
          )}
          {social.linkedin && (
            <a href={normalizeUrl(social.linkedin)} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <FaLinkedin />
            </a>
          )}
          {social.website && (
            <a href={normalizeUrl(social.website)} target="_blank" rel="noopener noreferrer" aria-label="Website">
              <FaGlobe />
            </a>
          )}
          {social.tiktok && (
            <a href={normalizeUrl(social.tiktok)} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
              <FaTiktok />
            </a>
          )}
          {social.whatsapp && (
            <a href={normalizeUrl(social.whatsapp)} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
              <FaWhatsapp />
            </a>
          )}
                 </div>

        {/* Banners Carousel */}
        {banners.length > 0 && (
  <div className="banners-carousel">
    <div className="banners-container">
      {banners.map((banner, index) => {
        const imageUrl = banner.Image || banner.image;
        const isActive = index === currentBannerIndex;
        const prevIndex = (currentBannerIndex - 1 + banners.length) % banners.length;
        const nextIndex = (currentBannerIndex + 1) % banners.length;

        let posClass = 'banner-hidden';
        if (isActive) posClass = 'store-banner-center';
        else if (index === prevIndex) posClass = 'store-banner-previous';
        else if (index === nextIndex) posClass = 'store-banner-next';

        // handle clicks
        const handleClick = () => {
          if (index === prevIndex) {
            prevBanner();
          } else if (index === nextIndex) {
            nextBanner();
          }
        };

        return (
          <div
            key={banner.id}
            className={`banner-slide ${posClass}`}
            onClick={handleClick}
            style={{ cursor: (index === prevIndex || index === nextIndex) ? "pointer" : "default" }}
          >
            <div className="banner-card">
              <div className="banner-media">
                <img
                  src={imageUrl}
                  alt={`Banner ${index + 1}`}
                  className="banner-image"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  draggable="false"
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>

    {/* Navigation Arrows */}
    {banners.length > 1 && (
      <>
        <button className="banner-nav-btn banner-prev" onClick={prevBanner} aria-label="Previous banner">
          <svg className="chev" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </button>

        <button className="banner-nav-btn banner-next" onClick={nextBanner} aria-label="Next banner">
          <svg className="chev" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path d="M8.59 16.59 10 18l6-6-6-6-1.41 1.41L13.17 12z" />
          </svg>
        </button>
      </>
    )}

    {/* Pagination Dots */}
    {banners.length > 1 && (
      <div className="banner-dots">
        {banners.map((_, index) => (
          <button
            key={index}
            className={`banner-dot ${index === currentBannerIndex ? 'active' : ''}`}
            onClick={() => goToBanner(index)}
            aria-label={`Go to banner ${index + 1}`}
          />
        ))}
      </div>
    )}
  </div>
)}




                     {/* Categories Section */}
           {categories.length > 0 && (
             <div className="ps-categories">
               <div className="categories-scroll-container">
                 <div className="categories-grid">
                   {categories.map((category) => (
                     <div 
                       key={category.id} 
                       className={`category-card ${category.IsFeature ? 'featured' : ''} ${selectedCategoryId === category.id ? 'selected' : ''}`}
                       onClick={() => fetchCategoryProducts(category.id, category.displayName)}
                     >
                       <div className="category-image-container">
                         <img
                           src={category.Image}
                           alt={category.displayName}
                           className="category-image"
                           onError={(e) => {
                             e.currentTarget.src = "/default-category.png";
                           }}
                         />
                         {/* {category.IsFeature && (
                           <div className="featured-badge">
                             <span>Featured</span>
                           </div>
                         )} */}
                         {selectedCategoryId === category.id && (
                           <div className="selected-indicator">
                             <span>✓</span>
                           </div>
                         )}
                       </div>
                       <div className="category-info">
                         <h3 className="ps-category-name">{category.displayName}</h3>
                       </div>
                     </div>
                   ))}
                 </div>
               </div>
             </div>
           )}

           {/* Category Products Section */}
           {selectedCategory && (
             <div className="category-products-section">
               <div className="category-products-header">
                <h2 className="category-products-title">{selectedCategory.name}</h2>
                <span className="category-products-count">{(t.profileStore?.productsCount || "{{count}} products").replace("{{count}}", String(categoryProducts.length))}</span>
                 <button className="close-category-btn" onClick={clearCategorySelection}>
                   ×
                 </button>
               </div>
               
               {loadingProducts ? (
                <div className="products-loading">
                  <div className="loading-animation"></div>
                  <p>{t.profileStore?.loadingProducts || "Loading products..."}</p>
                </div>
               ) : categoryProducts.length > 0 ? (
                 <div className="products-grid">
                   {categoryProducts.map((product) => (
                     <ProductCard 
                       key={product.id} 
                       product={product} 
                       navigate={navigate} 
                       isAuthenticated={isAuthenticated}
                       onUnauthenticatedClick={() => setShowDownloadModal(true)}
                     />
                   ))}
                 </div>
               ) : (
                <div className="no-products">
                  <p>{t.profileStore?.noProductsInCategory || "No products found in this category."}</p>
                </div>
               )}
             </div>
           )}

           {/* Sectors Section */}
           {sectors.length > 0 && hasProducts && (
             <div className="ps-sectors">
              {loadingSectorProducts ? (
                <div className="sectors-loading">
                  <div className="loading-animation"></div>
                  <p>{t.profileStore?.sectorsLoading || "Loading sectors..."}</p>
                </div>
               ) : (
                 <div className="sectors-container">
                   {Object.keys(sectorProducts).map((sectorId) => {
                     const sectorData = sectorProducts[sectorId];
                     const sector = sectorData.sector;
                     const products = sectorData.products;
                     
                     return (
                       <div key={sectorId} className="sector-section">
                        <div className="sector-header">
                          <h3 className="sector-name">{sector.displayName}</h3>
                          <span className="sector-count">{(t.profileStore?.productsCount || "{{count}} products").replace("{{count}}", String(products.length))}</span>
                        </div>
                         <div className="sector-products-grid">
                           {products.map((product) => (
                             <ProductCard 
                               key={product.id} 
                               product={product} 
                               navigate={navigate} 
                               isAuthenticated={isAuthenticated}
                               onUnauthenticatedClick={() => setShowDownloadModal(true)}
                             />
                           ))}
                         </div>
                       </div>
                     );
                   })}
                 </div>
               )}
             </div>
           )}

        {/* Empty state */}
        {!hasProducts && (
          isOwnStore ? (
            <div className="store-onboarding">
              {/* <button
                className="onboard-plus"
                onClick={() => navigate('/create')}
                aria-label={t.profileStore?.start || 'Start'}
              >
                <FaPlus />
              </button> */}
              <div className="onboard-store-icon">
                <FaShoppingBag />
              </div>
              <h2 className="onboard-title">{t?.profileStore?.onboard?.title || 'Start Your Business Now'}</h2>
              <button
                className="onboard-start-btn"
                // onClick={() => navigate('/create')}
              >
                {t?.profileStore?.onboard?.startButton || 'Start'}
              </button>
            </div>
          ) : (
            <div className="coming-soon-container">
              <div className="shopping-icon">
                <FaShoppingBag />
              </div>
              <h2>{t.profileStore?.comingSoon || "COMING SOON"}</h2>
              <p>{t.profileStore?.waitForActivation || "Wait For Activation Soon"}</p>
              <div className="store-loading-dots">
                <div className="store-dot"></div>
                <div className="store-dot"></div>
                <div className="store-dot"></div>
              </div>
            </div>
          )
        )}
      </div>

      {/* Button Editor Modal */}
      {showButtonEditor && (
        <ButtonEditor 
          onClose={() => {
            setShowButtonEditor(false);
            setEditingButton(null);
          }}
          onSave={(button, action) => {
            let newButtons;
            if (action === 'update') {
              // Update existing button
              newButtons = customButtons.map(b => 
                b.id === button.id ? button : b
              );
              setCustomButtons(newButtons);
            } else {
              // Create new button
              newButtons = [...customButtons, button];
              setCustomButtons(newButtons);
            }
            
            // Immediately save to localStorage
            saveButtonsImmediately(newButtons);
            console.log('[BUTTONS] Button saved via editor:', action, button);
            
            setShowButtonEditor(false);
            setEditingButton(null);
          }}
          editingButton={editingButton}
        />
      )}

           {/* Custom Buttons inside banner for persistent positioning */}
           {customButtons.map((button) => (
             <div
               key={button.id}
               className={`custom-button ${editMode ? 'edit-mode' : ''}`}
               style={{
                 position: 'absolute',
                 left: `${button.x || 50}%`,
                 top: `${button.y || 50}%`,
                 width: button.width || 120,
                 height: button.height || 40,
                 backgroundColor: button.color || '#3b82f6',
                 borderRadius: `${typeof button.borderRadius === 'number' ? button.borderRadius : 8}px`,
                 transform: 'translate(-50%, -50%)',
                 zIndex: 7,
                 cursor: editMode ? 'move' : 'pointer'
               }}
               onMouseDown={(e) => handleButtonMouseDown(e, button.id)}
               onClick={(e) => handleButtonClick(button, e)}
             >
               <span style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>
                 {button.text || 'Button'}
               </span>
               {editMode && (
                 <button
                   className="delete-button-btn"
                   onClick={(e) => {
                     e.stopPropagation();
                     deleteButton(button.id);
                   }}
                   title="Delete Button"
                 >
                   ×
                 </button>
               )}
             </div>
           ))}

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div className="ps-fullscreen-modal" onClick={closeFullscreen}>
          <div className="ps-fullscreen-content">
            <button className="ps-fullscreen-close" onClick={closeFullscreen}>
              ×
            </button>
            <div className="ps-fullscreen-frame" style={{ '--media-url': `url(${fullscreenImage})` }}>
              <img 
                src={fullscreenImage} 
                alt="Fullscreen" 
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        </div>
      )}

      {/* Download Modal for Unauthenticated Users */}
      {showDownloadModal && (
        <div className='ps-modal-overlay' onClick={() => setShowDownloadModal(false)}>
          <div className='ps-modal-container' onClick={(e) => e.stopPropagation()}>
            <h2 className='ps-modal-header'>
              {t?.downloadModal?.title || "Get iWinto"}
            </h2>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              {t?.downloadModal?.subtitle || "Download the app to view product details and make purchases"}
            </p>
            <div className='ps-store-buttons'>
              <a 
                href="https://apps.apple.com/ao/app/iwinto/id6482291656"
                rel="noopener noreferrer"
                className="ps-store-btn ps-store-btn-apple"
                target="_blank"
              >
                <FaApple size={20} /> {t?.downloadModal?.appStore || "App Store"}
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.winto.app"
                className="ps-store-btn ps-store-btn-google"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaGooglePlay size={20} /> {t?.downloadModal?.playStore || "Play Store"}
              </a>
            </div>
            <button className="ps-download-close-btn" onClick={() => setShowDownloadModal(false)}>
              {t?.downloadModal?.close || "Close"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
